// src/pages/AboutPage.jsx
import React from 'react';
import { Container, Row, Col, Button, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBrain, faUtensils, faDumbbell, faBalanceScaleRight, faHeartbeat,
    faUserCheck, faCameraRetro, faShieldAlt, faLightbulb, faChartLine, faPlayCircle,
    faLeaf, faRobot, faSeedling, faSearchPlus, faClipboardList
} from '@fortawesome/free-solid-svg-icons';
// PropTypes ya no es necesario aquí si FeatureCard se importa
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import FeatureCard from '../components/ui/FeatureCard'; // <-- IMPORTA EL REUTILIZABLE

const AboutPage = () => {
    const appName = "MyFit Menu AI"; // O el nombre de tu app

    const keyFeatures = [
        {
            icon: faUtensils,
            title: "AI-Powered Meal Personalization",
            text: `Receive daily meal plans and delicious recipes generated by AI, perfectly balanced for your unique macro and calorie targets, dietary needs, and taste preferences with ${appName}.`
        },
        {
            icon: faDumbbell,
            title: "Customized Fitness Routines",
            text: `Achieve your fitness aspirations with AI-generated workout plans from ${appName} that adapt to your level, goals, and available equipment, ensuring progress and variety.`
        },
        {
            icon: faCameraRetro,
            title: "Instant Food & Barcode Scanner",
            text: `Snap a photo of your meal or scan a barcode with ${appName} to get instant, AI-driven nutritional breakdowns and make smarter food choices effortlessly.`
        },
        {
            icon: faBalanceScaleRight,
            title: "Precise Macro & Protein Tracking",
            text: `Based on your profile, ${appName} precisely calculates your optimal daily intake for protein and other macronutrients, helping you fuel your body correctly.`
        },
        {
            icon: faChartLine,
            title: "Intelligent Progress Monitoring",
            text: `Track your journey with insightful analytics and visual progress reports. Stay motivated as ${appName}'s AI helps you understand your achievements and adjust your path.`
        },
        {
            icon: faSeedling,
            title: "Healthy Habit Formation",
            text: `More than just numbers, ${appName} guides you towards building sustainable, healthy habits for long-term well-being, supported by AI-driven tips and encouragement.`
        }
    ];

    const howItWorksSteps = [
        {
            icon: faUserCheck,
            title: "1. Tell Us About You",
            text: "Complete your profile with your age, metrics, activity level, dietary preferences (including allergies or restrictions), and your specific health and fitness goals."
        },
        {
            icon: faBrain,
            title: "2. AI Gets to Work",
            text: `Our intelligent algorithms analyze your unique data, calculating your precise macronutrient needs and identifying the best nutritional and fitness strategies for you.`
        },
        {
            icon: faClipboardList,
            title: "3. Receive Your Custom Plans",
            text: `Get personalized meal plans, delicious AI-generated recipes, and effective workout routines tailored to your objectives and lifestyle.`
        },
        {
            icon: faSearchPlus,
            title: "4. Scan & Understand",
            text: `Use our food image and barcode scanner for instant nutritional insights on the go. Make informed choices anytime, anywhere.`
        }
    ];

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            <main className="flex-grow-1">
                {/* Hero Section */}
                <section className="py-5 text-center bg-primary-light">
                    <Container>
                        <Row className="justify-content-center">
                            <Col md={9}>
                                <FontAwesomeIcon icon={faRobot} size="3x" className="text-primary mb-3" />
                                <h1 className="display-3 fw-bolder mb-3">About {appName}: Your AI-Powered Wellness Partner</h1>
                                <p className="lead text-secondary mb-4 fs-5">
                                    Unlock your peak health with intelligent, personalized nutrition and fitness plans.
                                    Discover what you eat, how to train, and achieve your goals—effortlessly.
                                </p>
                                <Button as={Link} to="/register" variant="primary" size="lg" className="px-5 py-3 shadow-sm fw-bold">
                                    Start Your Transformation
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Our Vision Section */}
                <section className="py-5">
                    <Container>
                        <Row className="align-items-center g-5">
                            <Col lg={6}>
                                <Image
                                    src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Zml0bmVzcyUyMGFpJTIwYXBwfGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60"
                                    alt={`${appName} - Smart Health Solutions`}
                                    fluid
                                    rounded
                                    className="shadow-lg"
                                />
                            </Col>
                            <Col lg={6}>
                                <h2 className="display-5 fw-bold mb-4">Our Vision: Democratizing Personalized Wellness</h2>
                                <p className="fs-5 text-muted mb-3">
                                    At {appName}, we're on a mission to make peak health and optimal nutrition accessible to everyone, everywhere. We believe that understanding your body and fueling it correctly shouldn't be a complex puzzle or a luxury.
                                </p>
                                <p className="fs-5 text-muted mb-3">
                                    That's why we've harnessed the power of cutting-edge Artificial Intelligence to create a seamless, intuitive platform that acts as your personal nutritionist, fitness coach, and food detective—all in one app. Say goodbye to generic plans and hello to a wellness journey designed exclusively for you.
                                </p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* How It Works Section - usando FeatureCard */}
                <section className="py-5 bg-light">
                    <Container>
                        <h2 className="text-center display-5 fw-bold mb-5">Your Journey with {appName}: Simple & Effective</h2>
                        <Row className="g-4 justify-content-center">
                            {howItWorksSteps.map((step, index) => (
                                <Col md={6} lg={3} key={`how-${index}`} className="d-flex">
                                    <FeatureCard
                                        icon={step.icon}
                                        title={step.title}
                                        text={step.text}
                                        iconContainerClass="icon-circle bg-success text-white mb-3 mx-auto"
                                        titleAs="h3"
                                        titleClassName="fw-bold fs-4"
                                        textClassName="text-muted"
                                        cardClassName="h-100 text-center shadow-sm border-0 p-3 feature-card-reusable"
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* Key Features Section - usando FeatureCard */}
                <section className="py-5">
                    <Container>
                        <h2 className="text-center display-5 fw-bold mb-5">Unlock Your Potential with {appName}'s Features</h2>
                        <Row className="g-4">
                            {keyFeatures.map((feature, index) => (
                                <Col md={6} lg={4} key={`feature-${index}`} className="d-flex">
                                    <FeatureCard
                                        icon={feature.icon}
                                        title={feature.title}
                                        text={feature.text}
                                        iconContainerClass="feature-card-icon bg-primary-light mx-auto"
                                        iconColorClass="text-primary"
                                        titleAs="h4"
                                        titleClassName="fw-bold mb-3"
                                        cardClassName="h-100 text-center shadow-sm border-0 p-4 feature-card-reusable"
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* Call to Action - MODIFICADO */}
                <section className="py-5 text-center final-cta-section"> {/* Clase para el fondo gradiente */}
                    <Container className="position-relative" style={{ zIndex: 1 }}> {/* Para que el contenido esté sobre el fondo */}
                        <Row className="justify-content-center">
                            <Col md={8} className="text-white"> {/* Texto blanco para contraste */}
                                <FontAwesomeIcon icon={faLightbulb} size="3x" className="text-warning mb-3" /> {/* Ícono de bombilla */}
                                <h2 className="display-4 fw-bolder mb-3">Ready to Redefine Your Wellness?</h2>
                                <p className="lead fs-5 mb-4" style={{ color: 'rgba(255,255,255,0.85)' }}> {/* Texto lead más claro */}
                                    Take control of your health with the power of personalized AI.
                                    Join {appName} today and discover a smarter way to eat, train, and live.
                                </p>
                                <Button
                                    as={Link}
                                    to="/register" // Enlace a la página de registro
                                    variant="light" // Variante 'light' o 'warning' para que destaque sobre fondo oscuro/primario
                                    size="lg"
                                    className="px-5 py-3 fw-bold rounded-pill shadow-lg cta-main-button" // Clases consistentes
                                >
                                    <FontAwesomeIcon icon={faPlayCircle} className="me-2" />
                                    Join {appName} Now - It's Free!
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </main>
            <Footer />

            {/* Estilos Globales o específicos para esta página */}
            <style jsx global>{`
                .bg-primary-light {
                    background-color: #e7f1ff; 
                }
                .icon-circle {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                }
                .bg-success.text-white .svg-inline--fa { 
                    color: white !important;
                }
                .feature-card-icon { /* Para la sección KeyFeatures */
                    width: 64px;
                    height: 64px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    margin-bottom: 1rem; /* Ajustado desde FeatureCard original */
                }
                .feature-card-reusable:hover { /* Nombre de clase usado en FeatureCard y HowItWorks */
                    transform: translateY(-5px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                    transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;
                }

                /* Estilos para la nueva sección CTA final */
                .final-cta-section {
                    /* Gradiente similar al de tu CallToAction de ejemplo, o tu color primario */
                    background: linear-gradient(135deg, var(--bs-primary, #0d6efd) 0%, var(--bs-info, #0dcaf0) 100%); 
                    /* O usa directamente tu color primario si lo prefieres sólido: */
                    /* background-color: var(--bs-primary, #0d6efd); */
                    position: relative; /* Para pseudo-elementos si los usas (ej. patrones de fondo) */
                }

                .cta-main-button { /* Reutiliza el nombre de clase de tu ejemplo */
                    transition: transform 0.2s ease-out, box-shadow 0.2s ease-out !important;
                    border: 2px solid transparent; /* Para un posible efecto outline en hover */
                }
                .cta-main-button:hover {
                    transform: translateY(-3px) scale(1.03);
                    box-shadow: 0 0.75rem 1.5rem rgba(0,0,0,0.2) !important;
                }
                .cta-main-button.btn-light:hover {
                     background-color: #f8f9fa; /* Un poco más oscuro para light */
                     border-color: #f8f9fa;
                     color: var(--bs-primary-darker, #0a58ca); /* Ajusta el color del texto en hover */
                }
                 .cta-main-button.btn-warning:hover {
                    background-color: #ffb700; /* Más oscuro para warning */
                    border-color: #ffb700;
                }


            `}</style>
        </div>
    );
};
// Ya no es necesario definir FeatureCard ni PropTypes aquí.

export default AboutPage;